import { c as _c } from "react/compiler-runtime";
import type { ApiNodeDataBankLink, BankLinkProduct } from '@onefootprint/request-types/dashboard';
import { Form } from '@onefootprint/ui';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import useDirty from '../../hooks/use-dirty';
import BaseNode, { type ApplyEditMutation } from '../base-node';
type BankLinkFormData = {
  redirectUri: string;
  alias: string | undefined;
  blockedInstitutions: string;
  accountNumbers: boolean;
  transactions: boolean;
  investments: boolean;
  identity: boolean;
  waitForRequiredProductData: boolean;
  permitOnlyDepositoryAccounts: boolean;
  permitOnlyOneAccount: boolean;
};
const BankLinkNode = node => {
  const $ = _c(7);
  let t0;
  if ($[0] !== node.data || $[1] !== node.id) {
    t0 = {
      render: t1 => {
        const {
          mutation,
          canEdit
        } = t1;
        return <Content mutation={mutation} nodeData={node.data} nodeId={node.id} canEdit={canEdit} />;
      },
      form: "bank-link-form"
    };
    $[0] = node.data;
    $[1] = node.id;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== node.data || $[4] !== node.id || $[5] !== t0) {
    t1 = <BaseNode nodeId={node.id} data={node.data} sidebar={t0} />;
    $[3] = node.data;
    $[4] = node.id;
    $[5] = t0;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const Content = t0 => {
  const $ = _c(160);
  const {
    mutation,
    nodeData,
    nodeId,
    canEdit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "bankLink"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    toggle
  } = useDirty();
  const t2 = nodeData.settings?.redirectUri || "";
  let t3;
  if ($[1] !== nodeData.settings?.blockedInstitutions) {
    t3 = nodeData.settings?.blockedInstitutions?.join(", ") || "";
    $[1] = nodeData.settings?.blockedInstitutions;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== nodeData.settings?.requiredProducts) {
    t4 = nodeData.settings?.requiredProducts?.includes("account_numbers");
    $[3] = nodeData.settings?.requiredProducts;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== nodeData.settings?.requiredProducts) {
    t5 = nodeData.settings?.requiredProducts?.includes("transactions");
    $[5] = nodeData.settings?.requiredProducts;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== nodeData.settings?.requiredProducts) {
    t6 = nodeData.settings?.requiredProducts?.includes("investments");
    $[7] = nodeData.settings?.requiredProducts;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== nodeData.settings?.requiredProducts) {
    t7 = nodeData.settings?.requiredProducts?.includes("identity");
    $[9] = nodeData.settings?.requiredProducts;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = nodeData.settings?.waitForRequiredProductData ?? false;
  const t9 = nodeData.settings?.permitOnlyDepositoryAccounts ?? false;
  const t10 = nodeData.settings?.permitOnlyOneAccount ?? false;
  const t11 = nodeData.settings?.alias;
  let t12;
  if ($[11] !== t10 || $[12] !== t11 || $[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t12 = {
      defaultValues: {
        redirectUri: t2,
        blockedInstitutions: t3,
        accountNumbers: t4,
        transactions: t5,
        investments: t6,
        identity: t7,
        waitForRequiredProductData: t8,
        permitOnlyDepositoryAccounts: t9,
        permitOnlyOneAccount: t10,
        alias: t11
      }
    };
    $[11] = t10;
    $[12] = t11;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const methods = useForm(t12);
  const {
    handleSubmit,
    formState: t13
  } = methods;
  const {
    isDirty
  } = t13;
  let t14;
  if ($[22] !== isDirty || $[23] !== toggle) {
    t14 = () => {
      toggle("bank-link", isDirty);
    };
    $[22] = isDirty;
    $[23] = toggle;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== isDirty) {
    t15 = [isDirty];
    $[25] = isDirty;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  useEffect(t14, t15);
  let t16;
  if ($[27] !== mutation || $[28] !== nodeId) {
    t16 = async data => {
      const getBlockedInstitutions = () => {
        if (!data.blockedInstitutions || data.blockedInstitutions.length === 0) {
          return;
        }
        return data.blockedInstitutions.split(",").map(_temp).filter(_temp2);
      };
      const getRequiredProducts = () => {
        const requiredProducts = [];
        if (data.accountNumbers) {
          requiredProducts.push("account_numbers");
        }
        if (data.transactions) {
          requiredProducts.push("transactions");
        }
        if (data.investments) {
          requiredProducts.push("investments");
        }
        if (data.identity) {
          requiredProducts.push("identity");
        }
        return requiredProducts;
      };
      ;
      try {
        await mutation.mutateAsync({
          nodeId,
          kind: "update_node",
          nodeData: {
            kind: "bank_link",
            settings: {
              blockedInstitutions: getBlockedInstitutions(),
              fetchImmediately: true,
              redirectUri: data.redirectUri,
              requiredProducts: getRequiredProducts(),
              permitOnlyDepositoryAccounts: data.permitOnlyDepositoryAccounts,
              permitOnlyOneAccount: data.permitOnlyOneAccount,
              waitForRequiredProductData: data.waitForRequiredProductData,
              alias: data.alias
            }
          }
        });
      } catch (t17) {
        const _ = t17;
      }
    };
    $[27] = mutation;
    $[28] = nodeId;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const handleFormSubmit = t16;
  let t17;
  if ($[30] !== handleFormSubmit || $[31] !== handleSubmit) {
    t17 = handleSubmit(handleFormSubmit);
    $[30] = handleFormSubmit;
    $[31] = handleSubmit;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t) {
    t18 = t(_temp3);
    $[33] = t;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t18) {
    t19 = <span className="text-body-2 text-primary">{t18}</span>;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = !canEdit;
  let t21;
  if ($[37] !== t) {
    t21 = t(_temp4);
    $[37] = t;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t) {
    t22 = t(_temp5);
    $[39] = t;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t) {
    t23 = t(_temp6);
    $[41] = t;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t) {
    t24 = t(_temp7);
    $[43] = t;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t24) {
    t25 = {
      required: t24
    };
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t20 || $[48] !== t21 || $[49] !== t22 || $[50] !== t23 || $[51] !== t25) {
    t26 = <Form.Input name="redirectUri" disabled={t20} label={t21} placeholder={t22} hint={t23} size="compact" rules={t25} />;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  const t27 = !canEdit;
  let t28;
  if ($[53] !== t) {
    t28 = t(_temp8);
    $[53] = t;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t) {
    t29 = t(_temp9);
    $[55] = t;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t) {
    t30 = t(_temp10);
    $[57] = t;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t) {
    t31 = t(_temp11);
    $[59] = t;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] !== t31) {
    t32 = {
      required: t31
    };
    $[61] = t31;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== t27 || $[64] !== t28 || $[65] !== t29 || $[66] !== t30 || $[67] !== t32) {
    t33 = <Form.Input name="alias" disabled={t27} label={t28} placeholder={t29} hint={t30} size="compact" rules={t32} />;
    $[63] = t27;
    $[64] = t28;
    $[65] = t29;
    $[66] = t30;
    $[67] = t32;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] !== t) {
    t34 = t(_temp12);
    $[69] = t;
    $[70] = t34;
  } else {
    t34 = $[70];
  }
  let t35;
  if ($[71] !== t34) {
    t35 = <h3 className="text-label-2 text-primary">{t34}</h3>;
    $[71] = t34;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] !== t) {
    t36 = t(_temp13);
    $[73] = t;
    $[74] = t36;
  } else {
    t36 = $[74];
  }
  const t37 = !canEdit;
  let t38;
  if ($[75] !== t36 || $[76] !== t37) {
    t38 = <Form.Checkbox name="accountNumbers" label={t36} disabled={t37} />;
    $[75] = t36;
    $[76] = t37;
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  let t39;
  if ($[78] !== t) {
    t39 = t(_temp14);
    $[78] = t;
    $[79] = t39;
  } else {
    t39 = $[79];
  }
  const t40 = !canEdit;
  let t41;
  if ($[80] !== t39 || $[81] !== t40) {
    t41 = <Form.Checkbox name="transactions" label={t39} disabled={t40} />;
    $[80] = t39;
    $[81] = t40;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  let t42;
  if ($[83] !== t) {
    t42 = t(_temp15);
    $[83] = t;
    $[84] = t42;
  } else {
    t42 = $[84];
  }
  const t43 = !canEdit;
  let t44;
  if ($[85] !== t42 || $[86] !== t43) {
    t44 = <Form.Checkbox name="investments" label={t42} disabled={t43} />;
    $[85] = t42;
    $[86] = t43;
    $[87] = t44;
  } else {
    t44 = $[87];
  }
  let t45;
  if ($[88] !== t) {
    t45 = t(_temp16);
    $[88] = t;
    $[89] = t45;
  } else {
    t45 = $[89];
  }
  const t46 = !canEdit;
  let t47;
  if ($[90] !== t45 || $[91] !== t46) {
    t47 = <Form.Checkbox name="identity" label={t45} disabled={t46} />;
    $[90] = t45;
    $[91] = t46;
    $[92] = t47;
  } else {
    t47 = $[92];
  }
  let t48;
  if ($[93] !== t38 || $[94] !== t41 || $[95] !== t44 || $[96] !== t47) {
    t48 = <div className="flex flex-col gap-3">{t38}{t41}{t44}{t47}</div>;
    $[93] = t38;
    $[94] = t41;
    $[95] = t44;
    $[96] = t47;
    $[97] = t48;
  } else {
    t48 = $[97];
  }
  let t49;
  if ($[98] !== t35 || $[99] !== t48) {
    t49 = <div className="flex flex-col gap-4">{t35}{t48}</div>;
    $[98] = t35;
    $[99] = t48;
    $[100] = t49;
  } else {
    t49 = $[100];
  }
  let t50;
  if ($[101] !== t) {
    t50 = t(_temp17);
    $[101] = t;
    $[102] = t50;
  } else {
    t50 = $[102];
  }
  let t51;
  if ($[103] !== t50) {
    t51 = <h3 className="text-label-2 text-primary">{t50}</h3>;
    $[103] = t50;
    $[104] = t51;
  } else {
    t51 = $[104];
  }
  let t52;
  if ($[105] !== t) {
    t52 = t(_temp18);
    $[105] = t;
    $[106] = t52;
  } else {
    t52 = $[106];
  }
  let t53;
  if ($[107] !== t) {
    t53 = t(_temp19);
    $[107] = t;
    $[108] = t53;
  } else {
    t53 = $[108];
  }
  const t54 = !canEdit;
  let t55;
  if ($[109] !== t52 || $[110] !== t53 || $[111] !== t54) {
    t55 = <Form.Checkbox name="waitForRequiredProductData" label={t52} hint={t53} disabled={t54} />;
    $[109] = t52;
    $[110] = t53;
    $[111] = t54;
    $[112] = t55;
  } else {
    t55 = $[112];
  }
  let t56;
  if ($[113] !== t) {
    t56 = t(_temp20);
    $[113] = t;
    $[114] = t56;
  } else {
    t56 = $[114];
  }
  let t57;
  if ($[115] !== t) {
    t57 = t(_temp21);
    $[115] = t;
    $[116] = t57;
  } else {
    t57 = $[116];
  }
  const t58 = !canEdit;
  let t59;
  if ($[117] !== t56 || $[118] !== t57 || $[119] !== t58) {
    t59 = <Form.Checkbox name="permitOnlyDepositoryAccounts" label={t56} hint={t57} disabled={t58} />;
    $[117] = t56;
    $[118] = t57;
    $[119] = t58;
    $[120] = t59;
  } else {
    t59 = $[120];
  }
  let t60;
  if ($[121] !== t) {
    t60 = t(_temp22);
    $[121] = t;
    $[122] = t60;
  } else {
    t60 = $[122];
  }
  let t61;
  if ($[123] !== t) {
    t61 = t(_temp23);
    $[123] = t;
    $[124] = t61;
  } else {
    t61 = $[124];
  }
  const t62 = !canEdit;
  let t63;
  if ($[125] !== t60 || $[126] !== t61 || $[127] !== t62) {
    t63 = <Form.Checkbox name="permitOnlyOneAccount" label={t60} hint={t61} disabled={t62} />;
    $[125] = t60;
    $[126] = t61;
    $[127] = t62;
    $[128] = t63;
  } else {
    t63 = $[128];
  }
  let t64;
  if ($[129] !== t51 || $[130] !== t55 || $[131] !== t59 || $[132] !== t63) {
    t64 = <div className="flex flex-col gap-4">{t51}{t55}{t59}{t63}</div>;
    $[129] = t51;
    $[130] = t55;
    $[131] = t59;
    $[132] = t63;
    $[133] = t64;
  } else {
    t64 = $[133];
  }
  const t65 = !canEdit;
  let t66;
  if ($[134] !== t) {
    t66 = t(_temp24);
    $[134] = t;
    $[135] = t66;
  } else {
    t66 = $[135];
  }
  let t67;
  if ($[136] !== t) {
    t67 = t(_temp25);
    $[136] = t;
    $[137] = t67;
  } else {
    t67 = $[137];
  }
  let t68;
  if ($[138] !== t) {
    t68 = t(_temp26);
    $[138] = t;
    $[139] = t68;
  } else {
    t68 = $[139];
  }
  let t69;
  if ($[140] !== t65 || $[141] !== t66 || $[142] !== t67 || $[143] !== t68) {
    t69 = <Form.Input name="blockedInstitutions" disabled={t65} label={t66} placeholder={t67} hint={t68} size="compact" />;
    $[140] = t65;
    $[141] = t66;
    $[142] = t67;
    $[143] = t68;
    $[144] = t69;
  } else {
    t69 = $[144];
  }
  let t70;
  if ($[145] !== t26 || $[146] !== t33 || $[147] !== t49 || $[148] !== t64 || $[149] !== t69) {
    t70 = <div className="flex flex-col gap-6">{t26}{t33}{t49}{t64}{t69}</div>;
    $[145] = t26;
    $[146] = t33;
    $[147] = t49;
    $[148] = t64;
    $[149] = t69;
    $[150] = t70;
  } else {
    t70 = $[150];
  }
  let t71;
  if ($[151] !== t19 || $[152] !== t70) {
    t71 = <div className="px-5 py-4 border rounded bg-primary border-tertiary flex flex-col gap-6">{t19}{t70}</div>;
    $[151] = t19;
    $[152] = t70;
    $[153] = t71;
  } else {
    t71 = $[153];
  }
  let t72;
  if ($[154] !== t17 || $[155] !== t71) {
    t72 = <form id="bank-link-form" onSubmit={t17}>{t71}</form>;
    $[154] = t17;
    $[155] = t71;
    $[156] = t72;
  } else {
    t72 = $[156];
  }
  let t73;
  if ($[157] !== methods || $[158] !== t72) {
    t73 = <FormProvider {...methods}>{t72}</FormProvider>;
    $[157] = methods;
    $[158] = t72;
    $[159] = t73;
  } else {
    t73 = $[159];
  }
  return t73;
};
export default BankLinkNode;
function _temp(inst) {
  return inst.trim();
}
function _temp2(inst_0) {
  return inst_0.length > 0;
}
function _temp3($) {
  return $.instructions;
}
function _temp4($_0) {
  return $_0.form.redirectUri.label;
}
function _temp5($_1) {
  return $_1.form.redirectUri.placeholder;
}
function _temp6($_2) {
  return $_2.form.redirectUri.hint;
}
function _temp7($_3) {
  return $_3.form.redirectUri.required;
}
function _temp8($_4) {
  return $_4.form.alias.label;
}
function _temp9($_5) {
  return $_5.form.alias.placeholder;
}
function _temp10($_6) {
  return $_6.form.alias.hint;
}
function _temp11($_7) {
  return $_7.form.alias.required;
}
function _temp12($_8) {
  return $_8.form.products.title;
}
function _temp13($_9) {
  return $_9.form.products.accountNumbers;
}
function _temp14($_10) {
  return $_10.form.products.transactions;
}
function _temp15($_11) {
  return $_11.form.products.investments;
}
function _temp16($_12) {
  return $_12.form.products.identity;
}
function _temp17($_13) {
  return $_13.form.otherSettings.title;
}
function _temp18($_14) {
  return $_14.form.otherSettings.waitForRequiredProductData.label;
}
function _temp19($_15) {
  return $_15.form.otherSettings.waitForRequiredProductData.hint;
}
function _temp20($_16) {
  return $_16.form.otherSettings.permitOnlyDepositoryAccounts.label;
}
function _temp21($_17) {
  return $_17.form.otherSettings.permitOnlyDepositoryAccounts.hint;
}
function _temp22($_18) {
  return $_18.form.otherSettings.permitOnlyOneAccount.label;
}
function _temp23($_19) {
  return $_19.form.otherSettings.permitOnlyOneAccount.hint;
}
function _temp24($_20) {
  return $_20.form.blockedInstitutions.label;
}
function _temp25($_21) {
  return $_21.form.blockedInstitutions.placeholder;
}
function _temp26($_22) {
  return $_22.form.blockedInstitutions.hint;
}